import { useState } from "react";
import { BRIEF as C } from "../../constants/briefTheme";
import PPIcon from "./PPIcon";

// ─── AppRail ────────────────────────────────────────────────────────────────
// Left navigation rail. Replaces the old centered top-tab row: seven
// absolutely-centered tabs drifted as the left/right content changed and
// capped out at about that many items, while a rail scales, groups related
// sections, and frees the full page width for content.
//
// Sections are grouped Workspace (day-to-day) / Intelligence (analysis).
// Guests only ever see Pipeline + Map, matching the old guest tab set.
// The user menu and settings live at the bottom of the rail rather than in a
// top bar, so the rail owns identity and navigation together.

const ICONS = {
  home: (
    <path d="M3 9.5 12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1z" />
  ),
  pipeline: <path d="M3 5h18M3 12h12M3 19h7" />,
  // A paper plane: LOIs sent out through brokers.
  outreach: <><path d="M22 2 11 13" /><path d="m22 2-7 20-4-9-9-4z" /></>,
  sourcing: <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>,
  inbox: <><path d="M3 6.5h18v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" /><path d="m3 7 9 6 9-6" /></>,
  review: <path d="M4 20V9M10 20V4M16 20v-7M22 20v-4" />,
  comps: <><rect x="3" y="4" width="18" height="16" rx="1.5" /><path d="M3 10h18M9 10v10" /></>,
  map: <><path d="m3 6 6-2 6 2 6-2v14l-6 2-6-2-6 2z" /><path d="M9 4v14M15 6v14" /></>,
  settings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.9 1.2V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 7 19.4a1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.7 1.7 0 0 0 3 15a1.7 1.7 0 0 0-1.6-1H1a2 2 0 1 1 0-4h.4A1.7 1.7 0 0 0 3 9a1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A1.7 1.7 0 0 0 9 3a1.7 1.7 0 0 0 1-1.6V1a2 2 0 1 1 4 0v.4A1.7 1.7 0 0 0 15 3a1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1A1.7 1.7 0 0 0 21 9h.4a2 2 0 1 1 0 4H21a1.7 1.7 0 0 0-1.6 1z" /></>,
};

const GROUPS = [
  {
    label: "Workspace",
    items: [
      { key: "home",     label: "Home",     icon: "home" },
      { key: "pipeline", label: "Pipeline", icon: "pipeline", countKey: "activeDeals" },
      { key: "outreach", label: "Outreach", icon: "outreach", countKey: "openOutreach" },
      { key: "sourcing", label: "Sourcing", icon: "sourcing" },
      { key: "inbox",    label: "Inbox",    icon: "inbox", badgeKey: "inboxUnread" },
    ],
  },
  {
    label: "Intelligence",
    items: [
      { key: "review", label: "Review Pipeline", icon: "review" },
      { key: "comps",  label: "Comps",           icon: "comps" },
      { key: "map",    label: "Map",             icon: "map" },
    ],
  },
];

const GUEST_ITEMS = [
  { key: "pipeline", label: "Pipeline", icon: "pipeline" },
  { key: "map",      label: "Map",      icon: "map" },
];

function Icon({ name, color }) {
  return (
    <svg
      width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
      className="shrink-0"
    >
      {ICONS[name]}
    </svg>
  );
}

function NavItem({ item, active, counts, onClick }) {
  const badge = item.badgeKey ? counts?.[item.badgeKey] : null;
  const count = item.countKey ? counts?.[item.countKey] : null;
  return (
    <button
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className="w-full flex items-center gap-2.5 px-2 py-2 rounded-md text-[13px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-pp-red/60"
      style={{
        backgroundColor: active ? C.railActive : "transparent",
        color: active ? C.railTextOn : C.railText,
        fontWeight: active ? 600 : 500,
      }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = C.railHover; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = "transparent"; }}
    >
      <Icon name={item.icon} color={active ? C.accent : C.muted} />
      <span className="truncate">{item.label}</span>
      {badge > 0 && (
        <span
          className="ml-auto text-[10px] font-bold rounded-full px-1.5 tabular-nums"
          style={{ backgroundColor: C.accent, color: "#ffffff" }}
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
      {badge == null && count > 0 && (
        <span className="ml-auto text-[11px] tabular-nums" style={{ color: C.railGroup }}>{count}</span>
      )}
    </button>
  );
}

export default function AppRail({
  tab, onSelectTab, isGuest, counts,
  accountName, accountEmail, initials,
  onSignOut, onOpenAnalyst,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const groups = isGuest ? [{ label: null, items: GUEST_ITEMS }] : GROUPS;

  return (
    <aside
      data-print-hide
      className="w-[232px] shrink-0 flex flex-col sticky top-0 h-screen font-montserrat"
      style={{ backgroundColor: C.rail }}
    >
      {/* Brand */}
      <button
        onClick={() => onSelectTab(isGuest ? "pipeline" : "home")}
        className="flex items-center gap-2.5 px-5 pt-5 pb-[22px] text-left focus:outline-none"
      >
        <PPIcon size={24} variant="red" />
        <span className="flex flex-col gap-px">
          <span className="text-[14px] font-bold tracking-tight" style={{ color: C.railTextOn }}>Proxy</span>
          <span className="text-[9px] font-semibold uppercase" style={{ color: C.railGroup, letterSpacing: "0.12em" }}>
            ProximityParks
          </span>
        </span>
        {isGuest && (
          <span
            className="ml-auto text-[9px] font-bold uppercase px-1.5 py-0.5 rounded"
            style={{ letterSpacing: "0.1em", color: C.accent, backgroundColor: "rgba(233,53,41,0.12)" }}
          >
            Guest
          </span>
        )}
      </button>

      {/* Nav groups */}
      <nav className="flex flex-col gap-5 px-3">
        {groups.map((g, gi) => (
          <div key={g.label || gi} className="flex flex-col gap-0.5">
            {g.label && (
              <div
                className="px-2 pb-2 text-[9px] font-bold uppercase"
                style={{ letterSpacing: "0.14em", color: C.railGroup }}
              >
                {g.label}
              </div>
            )}
            {g.items.map(item => (
              <NavItem
                key={item.key}
                item={item}
                active={tab === item.key}
                counts={counts}
                onClick={() => onSelectTab(item.key)}
              />
            ))}
          </div>
        ))}
      </nav>

      {/* Analyst */}
      {!isGuest && onOpenAnalyst && (
        <div className="mt-auto px-3 pb-3">
          <button
            onClick={onOpenAnalyst}
            className="w-full flex items-center gap-2.5 p-2.5 rounded-lg text-left transition-colors"
            style={{ border: `1px solid ${C.railBorder}`, backgroundColor: "rgba(255,255,255,0.03)" }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = C.railHover; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.03)"; }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
              <path d="M4 5h16v11H9l-5 4z" />
            </svg>
            <span className="flex flex-col gap-px min-w-0">
              <span className="text-[12px] font-semibold" style={{ color: "#d8d8d8" }}>Ask the Analyst</span>
              {counts?.totalDeals > 0 && (
                <span className="text-[10px]" style={{ color: C.railGroup }}>
                  {counts.totalDeals.toLocaleString()} deals indexed
                </span>
              )}
            </span>
          </button>
        </div>
      )}

      {/* Settings + user */}
      <div className={`px-3 pb-4 ${!isGuest && onOpenAnalyst ? "" : "mt-auto"}`}>
        {!isGuest && (
          <NavItem
            item={{ key: "settings", label: "Settings", icon: "settings" }}
            active={tab === "settings"}
            onClick={() => onSelectTab("settings")}
          />
        )}

        <div className="relative mt-2 pt-3" style={{ borderTop: `1px solid ${C.railBorder}` }}>
          <button
            onClick={() => setMenuOpen(v => !v)}
            className="w-full flex items-center gap-2.5 text-left focus:outline-none"
          >
            <span
              className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0"
              style={{ backgroundColor: C.accent, color: "#ffffff" }}
            >
              {isGuest ? "G" : initials}
            </span>
            <span className="flex flex-col gap-px min-w-0">
              <span className="text-[12px] font-semibold truncate" style={{ color: "#d8d8d8" }}>
                {isGuest ? "Guest viewer" : (accountName || "Signed in")}
              </span>
              <span className="text-[10px] truncate" style={{ color: C.railGroup }}>
                {isGuest ? "View-only" : (accountEmail || "")}
              </span>
            </span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.railGroup} strokeWidth={2} strokeLinecap="round" className="ml-auto shrink-0">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>

          {menuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
              <div
                className="absolute left-0 right-0 bottom-full mb-2 z-50 rounded-lg shadow-xl py-1"
                style={{ backgroundColor: "#242424", border: `1px solid ${C.railBorder}` }}
              >
                <button
                  onClick={() => { setMenuOpen(false); onSignOut?.(); }}
                  className="w-full text-left px-3 py-2 text-[12px] font-medium transition-colors"
                  style={{ color: "#d8d8d8" }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = C.railHover; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                >
                  Sign out
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </aside>
  );
}
